<template>
  <div
    v-if="showing == 'react'"
    id="code-snippet-react"
    class="code-snippet-tab"
    role="tabpanel"
    aria-labelledby="tab-react"
  >
    <div class="padding-top-sm padding-x-xl">
      <ul
        class="code-snippet-secondary-tabs size-sm position-relative z-1"
        role="tablist"
        style="
          --list-style-type: none;
          --padding-left: 0;
          --margin-bottom: 0;
          --button-padding: var(--spacing-2xs) var(--spacing-sm);
          --button-border-radius: 0;
          --button-margin-bottom: 0;
        "
      >
        <li
          v-for="option in options"
          :key="option"
          style="--display: inline-block"
          role="presentation"
        >
          <button
            :id="`tab-${sluggifyOption(option)}`"
            class="link flat block code-snippet-secondary-tab"
            style="--button-active-background: transparent"
            type="button"
            role="tab"
            :aria-controls="`${sluggifyOption(option)}`"
            :aria-selected="syntaxChoice == sluggifyOption(option) ? 'true' : 'false'"
            @click="showOption(sluggifyOption(option))"
          >
            {{ option }}
          </button>
        </li>
      </ul>
    </div>

    <wa-code-block
      add-highlighting
      allow-shadow-copy
      copy-text="Copy Code Snippet"
      copy-tracking-event="icon_react_code_copy_button_click"
      :copy-tracking-params="{ icon_name: icon.name, icon_style: styling.style, icon_family: styling.family }"
      custom-copy-icon="faClone"
      lang="jsx"
      :code="codeSnippet"
    />

    <icon-code-snippet-links
      :has-custom-styling="hasCustomStyling"
      :links="reactLearnAboutLinks"
      :selected-style="styling.style"
      :version="version"
    />
  </div>
</template>

<script>
  import { camelCase, trimStart, upperFirst } from 'lodash'
  import { isDuotoneFamily } from 'app/releases.js'
  import { transformIconColoring } from './utils.js'
  import IconCodeSnippetLinks from './IconCodeSnippetLinks'
  import sluggify from 'app/utils/sluggify'

  export default {
    name: 'IconCodeSnippetReact',

    components: {
      IconCodeSnippetLinks
    },

    inject: ['currentVersion'],

    props: {
      defaultStyles: {
        type: Object
      },
      hasCustomStyling: {
        type: Boolean
      },
      icon: {
        type: Object
      },
      styling: {
        type: Object
      },
      translatedStyle: {
        required: true
      },
      showing: {
        required: false
      },
      version: {
        type: Number
      }
    },

    data() {
      return {
        syntaxChoice: 'global_import'
      }
    },

    computed: {
      reactLearnAboutLinks() {
        const label = 'Using Icons with React'
        let path = '/web/use-with/react/add-icons'
        let hash = ''

        if (this.version === 5) {
          path = '/web/use-with/react'

          hash = this.syntaxChoice === 'individual_import' ? '#using-icons-via-individual-use' : '#using-icons-via-global-use'
        }

        if (this.version !== 5) {
          switch (this.syntaxChoice) {
            case 'individual_import':
              hash = '#add-individual-icons-explicitly'
              break
            default:
              hash = '#add-icons-globally'
          }
        }

        return [{ label, path, hash }]
      },

      options() {
        return ['Global Import', 'Individual Import']
      },

      translatedAnimation() {
        if (!this.styling || !this.styling.animation) {
          return ''
        } else if (this.styling.animation === 'beat-fade') {
          return ` beatFade`
        } else if (this.styling.animation === 'spin-reverse') {
          return ` spin spinReverse`
        } else if (this.styling.animation === 'spin-pulse') {
          return ` spinPulse`
        } else {
          return ` ${this.styling.animation}`
        }
      },

      translatedRotation() {
        if (!this.styling || !this.styling.rotation) {
          return ''
        } else if (this.styling.rotation.includes('rotate')) {
          let rotate = this.styling.rotation.split('-')
          return ` rotation={${rotate[1]}}`
        } else if (this.hasCustomRotation) {
          return ` rotation={${this.styling.rotation}}`
        } else if (this.styling.rotation.includes('flip')) {
          let flip = this.styling.rotation.split('-')

          return ` flip="${flip[1]}"`
        } else {
          return ''
        }
      },

      hasCustomRotation() {
        return Number.isInteger(parseInt(this.styling?.rotation))
      },

      translatedSize() {
        if (!this.styling || !this.styling.size) {
          return ''
        } else {
          return ` size="${this.styling.size}"`
        }
      },

      colorPrimary() {
        if (this.styling && isDuotoneFamily(this.styling.family) && this.styling.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` "--fa-primary-color": "${this.styling.colorPrimary}",`
        } else if (this.styling && this.styling.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` color: "${this.styling.colorPrimary}",`
        } else {
          return ''
        }
      },

      colorSecondary() {
        if (this.styling && !isDuotoneFamily(this.styling.family)) {
          return ''
        } else if (this.styling && this.styling.colorSecondary !== this.defaultStyles.colorSecondary) {
          return ` "--fa-secondary-color": "${this.styling.colorSecondary}",`
        } else {
          return ''
        }
      },

      opacityPrimary() {
        if (this.styling && !isDuotoneFamily(this.styling.family)) {
          return ''
        } else if (this.styling && this.styling.opacityPrimary !== this.defaultStyles.opacityPrimary) {
          return ` "--fa-primary-opacity": "${this.styling.opacityPrimary}",`
        } else {
          return ''
        }
      },

      opacitySecondary() {
        if (this.styling && !isDuotoneFamily(this.styling.family)) {
          return ''
        } else if (this.styling && this.styling.opacitySecondary !== this.defaultStyles.opacitySecondary) {
          return ` "--fa-secondary-opacity": "${this.styling.opacitySecondary}",`
        } else {
          return ''
        }
      },

      translatedColors() {
        const stringStyle = transformIconColoring(
          this.styling,
          this.defaultStyles,
          this.colorPrimary,
          this.colorSecondary,
          this.opacityPrimary,
          this.opacitySecondary
        )

        if (stringStyle) {
          return ` style={{${trimStart(stringStyle)}}}`
        } else {
          return ''
        }
      },

      codeSnippet() {
        if (this.syntaxChoice === 'individual_import') {
          const iconName = upperFirst(camelCase(this.icon.name))

          return `<FontAwesomeIcon icon={fa${iconName}}${this.translatedAnimation}${this.translatedRotation}${this.translatedSize}${this.translatedColors} />`
        } else {
          return `<FontAwesomeIcon icon="${this.translatedStyle} fa-${this.icon.name}"${this.translatedAnimation}${this.translatedRotation}${this.translatedSize}${this.translatedColors} />`
        }
      }
    },

    methods: {
      copyReact() {
        this.$clipboard(
          `<FontAwesomeIcon icon="${this.translatedStyle} fa-${this.icon.name}"${this.translatedAnimation}${this.translatedRotation}${this.translatedSize} ${
            this.translatedColors ? this.translatedColors : ''
          }/>`
        )

        this.$emit('markCopied', 'react')
      },

      showOption(option) {
        this.syntaxChoice = option
      },

      sluggifyOption(option) {
        return sluggify(option)
      }
    }
  }
</script>
